"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Beer,
  CalendarDays,
  Clock,
  GlassWater,
  MapPin,
  Phone,
  Pizza,
  Quote,
  Trophy,
  UtensilsCrossed,
  Users,
  Wine,
} from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { getPublicPageBrand } from "@/lib/public-brand-theme";
import { RomaSectionHeader, RomaSubPageHero } from "@/components/roma-editorial";
import {
  aboutLocalTrustPoints,
  aboutSeoFaqs,
  romaAboutLocalTrustPoints,
  romaAboutSeoFaqs,
  type SeoContentBlock,
  type SeoFaq,
} from "@/lib/seo/content-quality";
import {
  romaKitchenAboutLocations,
  romaKitchenAboutStory,
  tuscanCoveAboutLocations,
  tuscanCoveAboutStory,
  type TuscanCoveAboutLocation,
} from "@/lib/tuscan-cove-content";

type AboutValue = {
  icon: typeof Beer;
  title: string;
  description: string;
};

const tuscanValues: AboutValue[] = [
  {
    icon: Beer,
    title: "Cold drinks",
    description:
      "Handcrafted cocktails, wine, draft beer, and a bar that feels easy to settle into.",
  },
  {
    icon: Pizza,
    title: "Real food",
    description:
      "Stone-fired pizza, burgers, wings, sharables, brunch, and comfort food made for the table.",
  },
  {
    icon: Trophy,
    title: "Game-day energy",
    description:
      "Big-screen viewing, bar specials, and a crowd-friendly room for football Sunday and playoff nights.",
  },
  {
    icon: Users,
    title: "Group-ready",
    description:
      "A polished but relaxed setting for family dinners, celebrations, private events, and catering.",
  },
];

// DRAFT Roma About copy — review voice before launch.
const romaValues: AboutValue[] = [
  {
    icon: UtensilsCrossed,
    title: "Chef-led kitchen",
    description:
      "Italian classics, pasta, antipasti boards, and charred mains from a kitchen that leads the menu.",
  },
  {
    icon: Wine,
    title: "Italian wine list",
    description:
      "An Italian list built to sit alongside the food, with pours by the glass and bottles for the table.",
  },
  {
    icon: GlassWater,
    title: "Unhurried rhythm",
    description:
      "A warm welcome, courses that take their time, and a dining room spaced for conversation.",
  },
  {
    icon: Users,
    title: "Group-ready",
    description:
      "Private dining for celebrations and catering for offices and homes at both locations.",
  },
];

const aboutImages = {
  patio:
    "https://static.spotapps.co/website_images/ab_websites/492492_website_v1/reservations_left.jpg",
  bar: "https://static.spotapps.co/spots/9f/0c909a348a461aae0e7db49c8ef6b7/wide_medium",
  diningRoom:
    "https://static.spotapps.co/spots/3e/2daf6d26724f7991aea68f7d322bb4/:original",
  pizza: "https://static.spotapps.co/spots/20/991a33e56f4116b108f23c04998ed6/full",
  burger: "https://static.spotapps.co/spots/5c/2973eb0bf546ceab2c1803a7d29a24/full",
  privateEvent:
    "https://static.spotapps.co/spots/bd/d716c5f44c47f8b264682c3a9c9f65/wide_medium",
} as const;

// DRAFT Roma About copy — review voice before launch.
const romaAboutImages = {
  hero:
    "https://static.spotapps.co/website_images/ab_websites/101218_website/about_right.jpg",
  primary:
    "https://static.spotapps.co/website_images/ab_websites/101218_website/about_left_new.jpg",
  secondary:
    "https://static.spotapps.co/website_images/ab_websites/101218_website/about_right.jpg",
} as const;

type ImageMoment = {
  src: string;
  alt: string;
  label: string;
};

type SisterRestaurantLink = {
  eyebrow: string;
  title: string;
  description: string;
  href: string;
  label: string;
};

type TechnologyPartnerLink = SisterRestaurantLink;

const tuscanImageMoments: ImageMoment[] = [
  {
    src: aboutImages.patio,
    alt: "Tuscan Cove Bar & Patio outdoor patio seating in Southern Highlands",
    label: "Patio dining",
  },
  {
    src: aboutImages.pizza,
    alt: "Stone-fired pizza from Tuscan Cove Bar & Patio",
    label: "Stone-fired pizza",
  },
  {
    src: aboutImages.privateEvent,
    alt: "Private event table set inside Tuscan Cove Bar & Patio",
    label: "Private events",
  },
];

// DRAFT Roma About copy — review voice before launch.
const romaImageMoments: ImageMoment[] = [
  {
    src: romaAboutImages.primary,
    alt: "Roma Kitchen private dining table with Italian place setting",
    label: "Private dining",
  },
  {
    src: romaAboutImages.secondary,
    alt: "Roma Kitchen Italian dishes plated for dinner",
    label: "Pasta",
  },
  {
    src: romaAboutImages.primary,
    alt: "Roma Kitchen dining room set for an unhurried Italian dinner",
    label: "Italian dining room",
  },
];

type AboutBrandContent = {
  heroEyebrow: string;
  heroImage: string;
  heroImageAlt: string;
  heroTitleBefore: string;
  heroTitleEmphasis: string;
  story:
    | typeof tuscanCoveAboutStory
    | typeof romaKitchenAboutStory;
  trustPoints: SeoContentBlock[];
  trustEyebrow: string;
  trustTitleBefore: string;
  trustTitleEmphasis: string;
  faqs: SeoFaq[];
  faqEyebrow: string;
  faqTitleBefore: string;
  faqTitleEmphasis: string;
  legacyImage: string;
  legacyImageAlt: string;
  barImage: string;
  barImageAlt: string;
  imageMoments: ImageMoment[];
  locations: TuscanCoveAboutLocation[];
  locationsEyebrow: string;
  locationsTitleBefore: string;
  locationsTitleEmphasis: string;
  locationsDescription: string;
  values: AboutValue[];
  valuesEyebrow: string;
  valuesTitleBefore: string;
  valuesTitleEmphasis: string;
  sisterRestaurant: SisterRestaurantLink;
  technologyPartner: TechnologyPartnerLink;
  ctaHeadingBefore: string;
  ctaHeadingEmphasis: string;
  ctaDescription: string;
  orderUrl: string;
  orderLabel: string;
};

const tuscanContent: AboutBrandContent = {
  heroEyebrow: "Southern Highlands · Las Vegas sports bar & patio",
  heroImage:
    "https://static.spotapps.co/spots/02/b566da6de740e19dcef9757aee532a/:original",
  heroImageAlt:
    "Tuscan Cove Bar & Patio dining room and patio in Southern Highlands, Las Vegas",
  heroTitleBefore: "About ",
  heroTitleEmphasis: "Tuscan Cove.",
  story: tuscanCoveAboutStory,
  trustPoints: aboutLocalTrustPoints,
  trustEyebrow: "Local proof",
  trustTitleBefore: "A Southern Highlands restaurant with ",
  trustTitleEmphasis: "real context.",
  faqs: aboutSeoFaqs,
  faqEyebrow: "Restaurant answers",
  faqTitleBefore: "Food, drinks, patio, and ",
  faqTitleEmphasis: "hours.",
  legacyImage: aboutImages.patio,
  legacyImageAlt:
    "Tuscan Cove Bar & Patio patio seating and outdoor dining area",
  barImage: aboutImages.bar,
  barImageAlt: "Tuscan Cove Bar & Patio bar seating area with TVs for game day",
  imageMoments: tuscanImageMoments,
  locations: tuscanCoveAboutLocations,
  locationsEyebrow: "Visit us",
  locationsTitleBefore: "One neighborhood, ",
  locationsTitleEmphasis: "many ways to gather.",
  locationsDescription:
    "Visit Tuscan Cove in Southern Highlands, reserve online, or reach out for catering and private parties.",
  values: tuscanValues,
  valuesEyebrow: "What we value",
  valuesTitleBefore: "The experience we want every guest to ",
  valuesTitleEmphasis: "feel.",
  sisterRestaurant: {
    eyebrow: "Sister restaurant",
    title: "Looking for a chef-led Italian dinner nearby?",
    description:
      "Chef's Roma Kitchen is part of the same Southern Highlands restaurant family, with Italian dining, private parties, catering, and reservations close to Tuscan Cove.",
    href: "https://chefsromakitchen.com/",
    label: "Chef's Roma Kitchen",
  },
  technologyPartner: {
    eyebrow: "Digital systems",
    title: "Guest systems shaped around real restaurant operations.",
    description:
      "Paxom supports the digital strategy, AI integration, and lead-generation systems behind the restaurant group's website, guest capture, and follow-up workflows.",
    href: "https://paxom.ai/",
    label: "Digital strategy and AI integration by Paxom",
  },
  ctaHeadingBefore: "Ready to order or plan your next ",
  ctaHeadingEmphasis: "gathering?",
  ctaDescription:
    "Menus, reservations, catering, and private events. Pick your path and we'll see you at the table.",
  orderUrl: "/order",
  orderLabel: "Order Online",
};

// DRAFT Roma About copy — review voice before launch.
const romaContent: AboutBrandContent = {
  heroEyebrow: "Chef-led Italian · Southern Highlands + Henderson",
  heroImage: romaAboutImages.hero,
  heroImageAlt: "Roma Kitchen Italian dishes and dining room in Las Vegas",
  heroTitleBefore: "About ",
  heroTitleEmphasis: "Roma Kitchen.",
  story: romaKitchenAboutStory,
  trustPoints: romaAboutLocalTrustPoints,
  trustEyebrow: "Local proof",
  trustTitleBefore: "A chef-led Italian restaurant with ",
  trustTitleEmphasis: "real context.",
  faqs: romaAboutSeoFaqs,
  faqEyebrow: "Restaurant answers",
  faqTitleBefore: "Kitchen, wine, reservations, and ",
  faqTitleEmphasis: "locations.",
  legacyImage: romaAboutImages.primary,
  legacyImageAlt: "Roma Kitchen private dining table with Italian place setting",
  barImage: romaAboutImages.secondary,
  barImageAlt: "Roma Kitchen Italian dining room with warm lighting",
  imageMoments: romaImageMoments,
  locations: romaKitchenAboutLocations,
  locationsEyebrow: "Visit us",
  locationsTitleBefore: "Two locations, ",
  locationsTitleEmphasis: "one Roma family.",
  locationsDescription:
    "Visit the Roma table closest to you, reserve online, or reach out for private dining and catering.",
  values: romaValues,
  valuesEyebrow: "What we value",
  valuesTitleBefore: "The dinner we want every guest to ",
  valuesTitleEmphasis: "feel.",
  sisterRestaurant: {
    eyebrow: "Sister restaurant",
    title: "Looking for a neighborhood bar and patio nearby?",
    description:
      "Tuscan Cove Bar & Patio is part of the same Southern Highlands restaurant family, with stone-fired pizza, cocktails, Sunday brunch, private events, and catering.",
    href: "https://tuscancove.com/",
    label: "Tuscan Cove Bar & Patio",
  },
  technologyPartner: {
    eyebrow: "Digital systems",
    title: "Digital hospitality systems built for two active restaurants.",
    description:
      "Paxom supports the digital strategy, AI integration, and lead-generation systems behind the restaurant group's website, guest capture, and follow-up workflows.",
    href: "https://paxom.ai/",
    label: "Digital strategy and AI integration by Paxom",
  },
  ctaHeadingBefore: "Ready to reserve or plan your next ",
  ctaHeadingEmphasis: "gathering?",
  ctaDescription:
    "Menus, reservations, catering, and private dining. Pick your path and we'll see you at the table.",
  orderUrl: "https://www.yelp.com/biz/roma-kitchen-las-vegas",
  orderLabel: "Reserve Now",
};

function StoryHeading({
  chapter,
  eyebrow,
  titleBefore,
  titleEmphasis,
}: {
  chapter: string;
  eyebrow: string;
  titleBefore: string;
  titleEmphasis: string;
}) {
  return (
    <RomaSectionHeader
      chapter={chapter}
      eyebrow={eyebrow}
      title={
        <>
          {titleBefore}
          <span className="italic text-[var(--roma-terracotta)]">{titleEmphasis}</span>
        </>
      }
    />
  );
}

export default function AboutContent() {
  const { siteConfig: activeSiteConfig } = useMarketingLocationContext();
  const pageBrand = getPublicPageBrand(activeSiteConfig);
  const isRoma = pageBrand === "chefs-roma";
  const content = isRoma ? romaContent : tuscanContent;
  const story = content.story;

  return (
    // Tuscan keeps the original markup (no brand attribute) so its tokens
    // continue resolving to the :root copper palette. Roma scopes the orange
    // tokens + Hanken font with data-public-page-brand="chefs-roma".
    <div
      className="roma-paper min-h-screen"
      data-public-page-brand={isRoma ? pageBrand : undefined}
    >
      <nav className="container mx-auto px-5 pt-4 sm:px-6 sm:pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>About</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </nav>

      <RomaSubPageHero
        eyebrow={content.heroEyebrow}
        image={content.heroImage}
        imageAlt={content.heroImageAlt}
        headingLevel="h1"
        title={
          <>
            {content.heroTitleBefore}
            <span className="italic text-[var(--roma-terracotta)]">{content.heroTitleEmphasis}</span>
          </>
        }
        description={story.heroDescription}
      />

      <section className="border-y border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] px-5 py-12 sm:px-8 md:py-16 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <RomaSectionHeader
            chapter="I"
            eyebrow={content.trustEyebrow}
            title={
              <>
                {content.trustTitleBefore}
                <span className="italic text-[var(--roma-terracotta)]">{content.trustTitleEmphasis}</span>
              </>
            }
          />
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {content.trustPoints.map((point) => (
              <article
                key={point.title}
                className="border-t border-[var(--roma-parchment-line)] pt-5"
              >
                <h2 className="font-serif text-2xl font-normal italic text-[var(--roma-espresso)]">
                  {point.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[var(--roma-espresso)]/72 md:text-base">
                  {point.body}
                </p>
              </article>
            ))}
          </div>
          <div className="mt-10 grid gap-8 border-t border-[var(--roma-parchment-line)] pt-8 md:grid-cols-2">
            {[content.sisterRestaurant, content.technologyPartner].map((contextLink) => (
              <article key={contextLink.eyebrow} className="space-y-3">
                <p className="roma-eyebrow text-[var(--roma-terracotta)]">
                  {contextLink.eyebrow}
                </p>
                <h2 className="max-w-xl font-serif text-2xl font-normal italic leading-tight text-[var(--roma-espresso)] md:text-3xl">
                  {contextLink.title}
                </h2>
                <p className="text-sm leading-relaxed text-[var(--roma-espresso)]/72 md:text-base">
                  {contextLink.description}
                </p>
                <a
                  href={contextLink.href}
                  className="roma-link inline-flex items-center gap-2 text-sm font-semibold text-[var(--roma-espresso)]"
                >
                  {contextLink.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 px-5 py-12 sm:px-8 md:py-16 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <RomaSectionHeader
            chapter="II"
            eyebrow={content.faqEyebrow}
            title={
              <>
                {content.faqTitleBefore}
                <span className="italic text-[var(--roma-terracotta)]">{content.faqTitleEmphasis}</span>
              </>
            }
          />
          <div className="mt-8 grid gap-x-8 gap-y-6 md:grid-cols-2">
            {content.faqs.map((faq) => (
              <article
                key={faq.question}
                className="border-t border-[var(--roma-parchment-line)] pt-5"
              >
                <h2 className="font-serif text-2xl font-normal text-[var(--roma-espresso)]">
                  {faq.question}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[var(--roma-espresso)]/72 md:text-base">
                  {faq.answer}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Legacy */}
      <section className="px-5 py-16 sm:px-8 md:py-24 lg:px-12">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-5"
          >
            <StoryHeading
              chapter="III"
              eyebrow={story.legacy.eyebrow}
              titleBefore={story.legacy.titleBefore}
              titleEmphasis={story.legacy.titleEmphasis}
            />
            {story.legacy.paragraphs.map((p, i) => (
              <p key={`legacy-${i}`} className="text-[var(--roma-espresso)]/72 leading-relaxed">
                {p}
              </p>
            ))}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.08 }}
            className={
              isRoma
                ? "overflow-hidden rounded-[var(--roma-radius)] border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/40 shadow-[0_26px_70px_-50px_rgb(15_14_14/0.45)]"
                : "overflow-hidden border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/40"
            }
          >
            <div className="relative aspect-[4/3] bg-[var(--roma-linen)]">
              <Image
                src={content.legacyImage}
                alt={content.legacyImageAlt}
                fill
                sizes="(max-width: 1024px) 100vw, 42vw"
                className="object-cover"
              />
            </div>
            <div className="p-8 md:p-10">
              <Quote
                className="mb-4 h-8 w-8 text-[var(--roma-terracotta)]"
                aria-hidden
              />
              <blockquote className="font-serif text-xl italic leading-snug text-[var(--roma-espresso)] md:text-2xl">
                &ldquo;{story.quote.text}&rdquo;
              </blockquote>
              <footer className="mt-6 text-sm text-[var(--roma-espresso)]/65">
                <cite className="font-semibold not-italic text-[var(--roma-espresso)]">
                  {story.quote.attribution}
                </cite>
                <span className="mt-1 block">{story.quote.attributionDetail}</span>
              </footer>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stewardship */}
      <section className="border-y border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] px-5 py-16 sm:px-8 md:py-20 lg:px-12">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.88fr_1.12fr] lg:items-center lg:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <StoryHeading
              chapter="IV"
              eyebrow={story.stewardship.eyebrow}
              titleBefore={story.stewardship.titleBefore}
              titleEmphasis={story.stewardship.titleEmphasis}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.06 }}
            className="grid gap-5 sm:grid-cols-2"
          >
            {story.stewardship.paragraphs.map((p, i) => (
              <p
                key={`stewardship-${i}`}
                className="border-t border-[var(--roma-parchment-line)] pt-5 text-sm leading-relaxed text-[var(--roma-espresso)]/72 md:text-base"
              >
                {p}
              </p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Evolution */}
      <section className="bg-[var(--roma-linen)]/45 px-5 py-16 sm:px-8 md:py-24 lg:px-12">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className={
              isRoma
                ? "overflow-hidden rounded-[var(--roma-radius)] border border-[var(--roma-parchment-line)] shadow-[0_26px_70px_-50px_rgb(15_14_14/0.45)]"
                : "overflow-hidden border border-[var(--roma-parchment-line)]"
            }
          >
            <Image
              src={content.barImage}
              alt={content.barImageAlt}
              width={1200}
              height={900}
              quality={85}
              className="h-full w-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-5"
          >
            <StoryHeading
              chapter="V"
              eyebrow={story.evolution.eyebrow}
              titleBefore={story.evolution.titleBefore}
              titleEmphasis={story.evolution.titleEmphasis}
            />
            {story.evolution.paragraphs.map((p, i) => (
              <p key={`evolution-${i}`} className="text-[var(--roma-espresso)]/72 leading-relaxed">
                {p}
              </p>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Hospitality */}
      <section className="px-5 py-16 sm:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-7xl space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <StoryHeading
              chapter="VI"
              eyebrow={story.hospitality.eyebrow}
              titleBefore={story.hospitality.titleBefore}
              titleEmphasis={story.hospitality.titleEmphasis}
            />
          </motion.div>
          <div className="grid gap-6 md:grid-cols-3" data-speakable="owner-bio">
            {story.hospitality.paragraphs.map((p, i) => (
              <motion.p
                key={`hospitality-${i}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="border-t border-[var(--roma-parchment-line)] pt-5 text-sm leading-relaxed text-[var(--roma-espresso)]/72 md:text-base"
              >
                {p}
              </motion.p>
            ))}
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {content.imageMoments.map((image, i) => (
              <motion.figure
                key={image.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className={
                  isRoma
                    ? "overflow-hidden rounded-[var(--roma-radius)] border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] shadow-[0_26px_70px_-50px_rgb(15_14_14/0.45)]"
                    : "overflow-hidden border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)]"
                }
              >
                <div className="relative aspect-[4/3] bg-[var(--roma-linen)]">
                  <Image
                    src={image.src}
                    alt={image.alt}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover"
                  />
                </div>
                <figcaption className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-[var(--roma-espresso)]/72">
                  <CalendarDays className="h-4 w-4 text-[var(--roma-terracotta)]" />
                  {image.label}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="bg-[var(--roma-linen)]/45 px-5 py-16 sm:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 max-w-2xl">
            <RomaSectionHeader
              chapter="VII"
              eyebrow={content.locationsEyebrow}
              title={
                <>
                  {content.locationsTitleBefore}
                  <span className="italic text-[var(--roma-terracotta)]">{content.locationsTitleEmphasis}</span>
                </>
              }
              description={content.locationsDescription}
            />
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {content.locations.map((loc) => (
              <article
                key={loc.id}
                className={
                  isRoma
                    ? "rounded-[var(--roma-radius)] border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] p-6 md:p-8 shadow-[0_26px_70px_-50px_rgb(15_14_14/0.45)]"
                    : "border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] p-6 md:p-8"
                }
              >
                <h3 className="font-serif text-2xl font-normal italic text-[var(--roma-espresso)]">
                  {loc.publicName}
                </h3>
                <p className="mt-3 flex items-start gap-2 text-sm text-[var(--roma-espresso)]/80">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[var(--roma-terracotta)]" />
                  <span>
                    {loc.addressLine1}
                    <br />
                    {loc.cityStateZip}
                  </span>
                </p>
                <a
                  href={loc.phoneHref}
                  className="roma-link mt-3 inline-flex items-center gap-2 text-sm font-medium text-[var(--roma-terracotta)]"
                >
                  <Phone className="h-4 w-4" />
                  {loc.phoneDisplay}
                </a>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-[var(--roma-espresso)]/65">
                  <Clock className="h-4 w-4 shrink-0" />
                  <ul className="list-inside list-disc space-y-1">
                    {loc.hoursLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
                <Link
                  href={loc.reservationUrl}
                  className="roma-link mt-5 inline-flex items-center gap-2 text-sm font-medium text-[var(--roma-espresso)]"
                >
                  Reserve a table
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="px-5 py-16 sm:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 max-w-2xl">
            <RomaSectionHeader
              chapter="VIII"
              eyebrow={content.valuesEyebrow}
              title={
                <>
                  {content.valuesTitleBefore}
                  <span className="italic text-[var(--roma-terracotta)]">{content.valuesTitleEmphasis}</span>
                </>
              }
            />
          </div>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {content.values.map((value) => {
              const Icon = value.icon;
              return (
                <article
                  key={value.title}
                  className="border-t border-[var(--roma-parchment-line)] pt-5"
                >
                  <Icon className="mb-5 h-6 w-6 text-[var(--roma-terracotta)]" />
                  <h3 className="mb-2 font-serif text-2xl font-normal italic text-[var(--roma-espresso)]">
                    {value.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-[var(--roma-espresso)]/68">
                    {value.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="roma-paper-dark roma-grain px-5 py-16 sm:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-6 border-t border-[var(--roma-ivory)]/15 pt-8 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-10">
            <div className="space-y-3">
              <h2 className="font-serif text-3xl font-normal text-[var(--roma-ivory)] sm:text-4xl">
                {content.ctaHeadingBefore}
                <span className="italic text-[var(--roma-saffron)]">{content.ctaHeadingEmphasis}</span>
              </h2>
              <p className="text-[var(--roma-ivory)]/72">
                {content.ctaDescription}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="roma-cta rounded-full">
                <a href={content.orderUrl} target="_blank" rel="noopener noreferrer">
                  {content.orderLabel}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="roma-cta-ghost rounded-full">
                <Link href="/private-events">Private Events</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
